import React, { useEffect, useState, useCallback } from 'react';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdRefresh, MdSettings,
  MdPeople, MdAssignment, MdHistory, MdWarning, MdCheck, MdClose,
  MdPerson, MdCategory, MdFolder, MdInfo
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import { recruiterAssignmentApi, jobMasterApi } from '../../../api/jobManagementApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './RecruiterAssignmentConfig.css';

const TABS = [
  { id: 'settings', label: 'Settings', icon: MdSettings },
  { id: 'recruiters', label: 'Recruiters', icon: MdPeople },
  { id: 'logs', label: 'Assignment Logs', icon: MdHistory },
  { id: 'alerts', label: 'Capacity Alerts', icon: MdWarning }
];

function RecruiterAssignmentConfig() {
  const [activeTab, setActiveTab] = useState('settings');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Settings state
  const [settings, setSettings] = useState({
    autoAssignmentEnabled: false,
    assignmentMode: 'VERTICAL_BASED',
    loadBalancingEnabled: true,
    defaultMaxActiveJrs: 15,
    fallbackToGlobal: true
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Recruiters state
  const [recruiters, setRecruiters] = useState([]);
  const [availableRecruiters, setAvailableRecruiters] = useState([]);
  const [showRecruiterModal, setShowRecruiterModal] = useState(false);
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [recruiterForm, setRecruiterForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Vertical/Project mappings
  const [verticals, setVerticals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingRecruiter, setMappingRecruiter] = useState(null);
  const [mappingType, setMappingType] = useState('vertical'); // 'vertical' or 'project'

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(0);
  const [totalLogsPages, setTotalLogsPages] = useState(0);

  // Alerts state
  const [alerts, setAlerts] = useState([]);

  // Stats state
  const [stats, setStats] = useState(null);

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      const response = await recruiterAssignmentApi.getSettings();
      if (response.success && response.data) {
        // Backend returns UPPER_SNAKE_CASE keys, transform to camelCase
        const data = response.data;
        setSettings({
          autoAssignmentEnabled: data.AUTO_ASSIGNMENT_ENABLED === 'true' || data.AUTO_ASSIGNMENT_ENABLED === true,
          assignmentMode: data.ASSIGNMENT_MODE || 'VERTICAL_BASED',
          loadBalancingEnabled: data.LOAD_BALANCING_ENABLED !== 'false' && data.LOAD_BALANCING_ENABLED !== false,
          defaultMaxActiveJrs: parseInt(data.DEFAULT_MAX_ACTIVE_JRS || '15', 10),
          fallbackToGlobal: data.FALLBACK_TO_GLOBAL !== 'false' && data.FALLBACK_TO_GLOBAL !== false
        });
      }
    } catch (e) {
      console.error('Failed to load settings:', e);
      toast.error('Failed to load settings');
    }
  }, []);

  // Load recruiters
  const loadRecruiters = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await recruiterAssignmentApi.getRecruiterConfigs();
      if (response.success) {
        setRecruiters(response.data || []);
      }
    } catch (e) {
      console.error('Failed to load recruiters:', e);
      toast.error('Failed to load recruiters');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load available recruiters (users with HR roles who are not configured yet)
  const loadAvailableRecruiters = async () => {
    try {
      const response = await recruiterAssignmentApi.getAvailableRecruiters();
      if (response.success) {
        setAvailableRecruiters(response.data || []);
      }
    } catch (e) {
      console.error('Failed to load available recruiters:', e);
    }
  };

  // Load verticals and projects for mappings
  const loadMasterData = async () => {
    try {
      const [vertResponse, projResponse] = await Promise.all([
        jobMasterApi.getVerticalsDropdown(),
        jobMasterApi.getProjectsDropdown()
      ]);
      if (vertResponse.success) {
        setVerticals(vertResponse.data || []);
      }
      if (projResponse.success) {
        setProjects(projResponse.data || []);
      }
    } catch (e) {
      console.error('Failed to load master data:', e);
    }
  };

  // Load logs
  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await recruiterAssignmentApi.getAssignmentLogs(logsPage, 20);
      if (response.success) {
        setLogs(response.data?.content || []);
        setTotalLogsPages(response.data?.totalPages || 0);
      }
    } catch (e) {
      console.error('Failed to load logs:', e);
      toast.error('Failed to load assignment logs');
    } finally {
      setIsLoading(false);
    }
  }, [logsPage]);

  // Load alerts
  const loadAlerts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await recruiterAssignmentApi.getCapacityAlerts();
      if (response.success && response.data) {
        // API returns { approachingCapacity: [...], atCapacity: [...] }
        // Combine both arrays for display
        const approaching = Array.isArray(response.data.approachingCapacity) ? response.data.approachingCapacity : [];
        const atCapacity = Array.isArray(response.data.atCapacity) ? response.data.atCapacity : [];
        setAlerts([...atCapacity, ...approaching]);
      } else {
        setAlerts([]);
      }
    } catch (e) {
      console.error('Failed to load alerts:', e);
      toast.error('Failed to load capacity alerts');
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load stats
  const loadStats = async () => {
    try {
      const response = await recruiterAssignmentApi.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  };

  // Initial load based on active tab
  useEffect(() => {
    loadMasterData();
    loadStats();
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case 'settings':
        loadSettings();
        break;
      case 'recruiters':
        loadRecruiters();
        loadAvailableRecruiters();
        break;
      case 'logs':
        loadLogs();
        break;
      case 'alerts':
        loadAlerts();
        break;
      default:
        break;
    }
  }, [activeTab, loadSettings, loadRecruiters, loadLogs, loadAlerts]);

  // Save settings
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      // Transform camelCase keys to UPPER_SNAKE_CASE for backend
      const backendSettings = {
        AUTO_ASSIGNMENT_ENABLED: String(settings.autoAssignmentEnabled),
        ASSIGNMENT_MODE: settings.assignmentMode,
        LOAD_BALANCING_ENABLED: String(settings.loadBalancingEnabled),
        DEFAULT_MAX_ACTIVE_JRS: String(settings.defaultMaxActiveJrs),
        FALLBACK_TO_GLOBAL: String(settings.fallbackToGlobal)
      };
      const response = await recruiterAssignmentApi.updateSettings(backendSettings);
      if (response.success) {
        toast.success('Settings saved successfully');
      } else {
        toast.error(response.message || 'Failed to save settings');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Open add recruiter modal
  const openAddRecruiterModal = () => {
    setEditingRecruiter(null);
    setRecruiterForm({
      recruiterId: '',
      maxActiveJRs: settings.defaultMaxActiveJrs || 15,
      participatesInAutoAssignment: true,
      emailNotificationsEnabled: true,
      inAppNotificationsEnabled: true,
      verticalIds: [],
      projectIds: []
    });
    setShowRecruiterModal(true);
  };

  // Open edit recruiter modal
  const openEditRecruiterModal = (recruiter) => {
    setEditingRecruiter(recruiter);
    setRecruiterForm({
      recruiterId: recruiter.recruiterId,
      recruiterName: recruiter.recruiterName,
      recruiterEmail: recruiter.recruiterEmail,
      maxActiveJRs: recruiter.maxActiveJRs || 15,
      participatesInAutoAssignment: recruiter.participatesInAutoAssignment !== false,
      emailNotificationsEnabled: recruiter.emailNotificationsEnabled !== false,
      inAppNotificationsEnabled: recruiter.inAppNotificationsEnabled !== false,
      verticalIds: recruiter.verticalIds || [],
      projectIds: recruiter.projectIds || []
    });
    setShowRecruiterModal(true);
  };

  // Toggle vertical selection
  const toggleVerticalSelection = (verticalId) => {
    setRecruiterForm(f => {
      const currentIds = f.verticalIds || [];
      if (currentIds.includes(verticalId)) {
        return { ...f, verticalIds: currentIds.filter(id => id !== verticalId) };
      } else {
        return { ...f, verticalIds: [...currentIds, verticalId] };
      }
    });
  };

  // Toggle project selection
  const toggleProjectSelection = (projectId) => {
    setRecruiterForm(f => {
      const currentIds = f.projectIds || [];
      if (currentIds.includes(projectId)) {
        return { ...f, projectIds: currentIds.filter(id => id !== projectId) };
      } else {
        return { ...f, projectIds: [...currentIds, projectId] };
      }
    });
  };

  // Handle recruiter form submit
  const handleRecruiterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let response;
      if (editingRecruiter) {
        response = await recruiterAssignmentApi.updateRecruiterConfig(editingRecruiter.recruiterId, recruiterForm);
        if (response.success) {
          toast.success('Recruiter configuration updated');
        }
      } else {
        response = await recruiterAssignmentApi.createRecruiterConfig(recruiterForm);
        if (response.success) {
          toast.success('Recruiter added to auto-assignment');
        }
      }

      if (response.success) {
        await loadRecruiters();
        await loadAvailableRecruiters();
        setShowRecruiterModal(false);
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete recruiter
  const handleDeleteRecruiter = async () => {
    if (!deleteConfirm) return;

    try {
      const response = await recruiterAssignmentApi.deleteRecruiterConfig(deleteConfirm.recruiterId);
      if (response.success) {
        toast.success('Recruiter removed from auto-assignment');
        await loadRecruiters();
        await loadAvailableRecruiters();
      } else {
        toast.error(response.message || 'Failed to remove recruiter');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove recruiter');
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Filter recruiters based on search
  const filteredRecruiters = recruiters.filter(r => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (r.recruiterName || '').toLowerCase().includes(searchLower) ||
      (r.recruiterEmail || '').toLowerCase().includes(searchLower)
    );
  });

  // Get capacity badge color
  const getCapacityBadge = (recruiter) => {
    const percentage = recruiter.capacityPercentage || 0;
    if (percentage >= 100) {
      return { class: 'capacity-full', text: 'Full' };
    } else if (percentage >= 80) {
      return { class: 'capacity-high', text: `${Math.round(percentage)}%` };
    } else if (percentage >= 50) {
      return { class: 'capacity-medium', text: `${Math.round(percentage)}%` };
    }
    return { class: 'capacity-low', text: `${Math.round(percentage)}%` };
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get assignment type label
  const getAssignmentTypeLabel = (type) => {
    const labels = {
      'AUTO_SINGLE': 'Auto (Single)',
      'AUTO_BULK': 'Auto (Bulk)',
      'MANUAL_SINGLE': 'Manual',
      'MANUAL_UPLOAD': 'Manual (Upload)',
      'REASSIGNED': 'Reassigned'
    };
    return labels[type] || type;
  };

  // Render Settings Tab
  const renderSettingsTab = () => (
    <div className="settings-container">
      <div className="settings-card">
        <div className="settings-header">
          <h3><MdSettings className="icon" /> Global Settings</h3>
          <p>Configure how job requisitions are automatically assigned to recruiters</p>
        </div>

        <div className="settings-form">
          <div className="setting-group">
            <div className="setting-toggle">
              <label className="toggle-label">
                <span className="toggle-text">
                  <strong>Enable Auto-Assignment</strong>
                  <span className="toggle-desc">Automatically assign new JRs to eligible recruiters</span>
                </span>
                <div className={`toggle-switch ${settings.autoAssignmentEnabled ? 'active' : ''}`}
                     onClick={() => setSettings(s => ({ ...s, autoAssignmentEnabled: !s.autoAssignmentEnabled }))}>
                  <div className="toggle-knob"></div>
                </div>
              </label>
            </div>
          </div>

          <div className="setting-group">
            <label className="setting-label">Assignment Mode</label>
            <select
              className="setting-select"
              value={settings.assignmentMode}
              onChange={(e) => setSettings(s => ({ ...s, assignmentMode: e.target.value }))}
            >
              <option value="VERTICAL_BASED">Vertical-Based</option>
              <option value="GLOBAL">Global Pool</option>
            </select>
            <span className="setting-hint">
              {settings.assignmentMode === 'VERTICAL_BASED'
                ? 'Recruiters are assigned based on their vertical mappings'
                : 'All eligible recruiters can receive any JR regardless of vertical'}
            </span>
          </div>

          <div className="setting-group">
            <div className="setting-toggle">
              <label className="toggle-label">
                <span className="toggle-text">
                  <strong>Load Balancing</strong>
                  <span className="toggle-desc">Assign JRs to recruiters with the least workload first</span>
                </span>
                <div className={`toggle-switch ${settings.loadBalancingEnabled ? 'active' : ''}`}
                     onClick={() => setSettings(s => ({ ...s, loadBalancingEnabled: !s.loadBalancingEnabled }))}>
                  <div className="toggle-knob"></div>
                </div>
              </label>
            </div>
          </div>

          <div className="setting-group">
            <label className="setting-label">Default Max Active JRs</label>
            <input
              type="number"
              className="setting-input"
              value={settings.defaultMaxActiveJrs}
              onChange={(e) => setSettings(s => ({ ...s, defaultMaxActiveJrs: parseInt(e.target.value) || 15 }))}
              min="1"
              max="100"
            />
            <span className="setting-hint">Default maximum active JRs per recruiter when adding new recruiters</span>
          </div>

          {settings.assignmentMode === 'VERTICAL_BASED' && (
            <div className="setting-group">
              <div className="setting-toggle">
                <label className="toggle-label">
                  <span className="toggle-text">
                    <strong>Fallback to Global</strong>
                    <span className="toggle-desc">Use global pool when no vertical-specific recruiters are available</span>
                  </span>
                  <div className={`toggle-switch ${settings.fallbackToGlobal ? 'active' : ''}`}
                       onClick={() => setSettings(s => ({ ...s, fallbackToGlobal: !s.fallbackToGlobal }))}>
                    <div className="toggle-knob"></div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div className="settings-actions">
            <button
              className="btn-save-settings"
              onClick={handleSaveSettings}
              disabled={isSavingSettings}
            >
              {isSavingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      {stats && (
        <div className="stats-card">
          <h3><MdInfo className="icon" /> Assignment Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-value">{stats.totalRecruiters || 0}</span>
              <span className="stat-label">Total Recruiters</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.activeRecruiters || 0}</span>
              <span className="stat-label">Active Recruiters</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.totalAutoAssigned || 0}</span>
              <span className="stat-label">Auto-Assigned Today</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.atCapacity || 0}</span>
              <span className="stat-label">At Capacity</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Recruiters Tab
  const renderRecruitersTab = () => (
    <div className="recruiters-container">
      <div className="table card">
        <div className="table-head">
          <div className="cell">Recruiter</div>
          <div className="cell">Max JRs</div>
          <div className="cell">Current JRs</div>
          <div className="cell">Capacity</div>
          <div className="cell">Status</div>
          <div className="cell">Last Assigned</div>
          <div className="cell" style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {isLoading && Array.from({ length: 5 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isLoading && filteredRecruiters.map((recruiter) => {
          const capacityBadge = getCapacityBadge(recruiter);
          return (
            <div className="table-row" key={recruiter.id || recruiter.recruiterId}>
              <div className="cell recruiter-info">
                <span className="recruiter-name">{recruiter.recruiterName || 'Unknown'}</span>
                <span className="recruiter-email">{recruiter.recruiterEmail || ''}</span>
              </div>
              <div className="cell">{recruiter.maxActiveJRs || 15}</div>
              <div className="cell">{recruiter.currentActiveJRs || 0}</div>
              <div className="cell">
                <span className={`capacity-badge ${capacityBadge.class}`}>
                  {capacityBadge.text}
                </span>
              </div>
              <div className="cell">
                {recruiter.participatesInAutoAssignment !== false ? (
                  <span className="status-badge active"><MdCheck /> Active</span>
                ) : (
                  <span className="status-badge inactive"><MdClose /> Paused</span>
                )}
              </div>
              <div className="cell">{formatDate(recruiter.lastAssignedAt)}</div>
              <div className="cell" style={{ textAlign: 'right', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                <button className="btn-action-compact btn-edit" onClick={() => openEditRecruiterModal(recruiter)} title="Edit">
                  <MdEdit />
                </button>
                <button className="btn-action-compact btn-delete" onClick={() => setDeleteConfirm(recruiter)} title="Remove">
                  <MdDelete />
                </button>
              </div>
            </div>
          );
        })}

        {!isLoading && filteredRecruiters.length === 0 && (
          <div className="empty-state">
            <MdPeople className="empty-icon" />
            <h3>No Recruiters Configured</h3>
            <p>Add recruiters to enable auto-assignment</p>
            <button className="btn-add-empty" onClick={openAddRecruiterModal}>
              <MdAdd /> Add Recruiter
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Render Logs Tab
  const renderLogsTab = () => (
    <div className="logs-container">
      <div className="table card">
        <div className="table-head">
          <div className="cell">JR Code</div>
          <div className="cell">Assigned To</div>
          <div className="cell">Previous</div>
          <div className="cell">Type</div>
          <div className="cell">Vertical</div>
          <div className="cell">Reason</div>
          <div className="cell">Date</div>
        </div>

        {isLoading && Array.from({ length: 5 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isLoading && logs.map((log) => (
          <div className="table-row" key={log.id}>
            <div className="cell log-jr-code">{log.requisitionCode || '-'}</div>
            <div className="cell">
              <span className="recruiter-name">{log.assignedRecruiterName || '-'}</span>
            </div>
            <div className="cell">
              {log.previousRecruiterName || '-'}
            </div>
            <div className="cell">
              <span className={`type-badge ${log.assignmentType?.toLowerCase()}`}>
                {getAssignmentTypeLabel(log.assignmentType)}
              </span>
            </div>
            <div className="cell">{log.verticalName || '-'}</div>
            <div className="cell log-reason" title={log.assignmentReason}>{log.assignmentReason || '-'}</div>
            <div className="cell">{formatDate(log.createdAt)}</div>
          </div>
        ))}

        {!isLoading && logs.length === 0 && (
          <div className="empty-state">
            <MdHistory className="empty-icon" />
            <h3>No Assignment Logs</h3>
            <p>Assignment logs will appear here once JRs are assigned</p>
          </div>
        )}
      </div>

      {totalLogsPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setLogsPage(p => Math.max(0, p - 1))}
            disabled={logsPage === 0}
          >
            Previous
          </button>
          <span className="pagination-info">Page {logsPage + 1} of {totalLogsPages}</span>
          <button
            className="pagination-btn"
            onClick={() => setLogsPage(p => Math.min(totalLogsPages - 1, p + 1))}
            disabled={logsPage >= totalLogsPages - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  // Render Alerts Tab
  const renderAlertsTab = () => {
    const alertsList = Array.isArray(alerts) ? alerts : [];
    return (
    <div className="alerts-container">
      {isLoading ? (
        <div className="loading-state">Loading alerts...</div>
      ) : alertsList.length === 0 ? (
        <div className="empty-state card">
          <MdCheck className="empty-icon success" />
          <h3>All Clear!</h3>
          <p>No recruiters are at or near capacity</p>
        </div>
      ) : (
        <div className="alerts-grid">
          {alertsList.map((alert) => {
            const capacityBadge = getCapacityBadge(alert);
            return (
              <div className={`alert-card ${capacityBadge.class}`} key={alert.recruiterId}>
                <div className="alert-header">
                  <MdWarning className="alert-icon" />
                  <span className={`capacity-badge ${capacityBadge.class}`}>
                    {capacityBadge.text}
                  </span>
                </div>
                <div className="alert-body">
                  <h4>{alert.recruiterName || 'Unknown Recruiter'}</h4>
                  <p className="alert-email">{alert.recruiterEmail}</p>
                  <div className="alert-stats">
                    <span>{alert.currentActiveJRs || 0} / {alert.maxActiveJRs || 15} JRs</span>
                  </div>
                </div>
                <div className="alert-actions">
                  <button className="btn-alert-action" onClick={() => openEditRecruiterModal(alert)}>
                    Adjust Capacity
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return renderSettingsTab();
      case 'recruiters':
        return renderRecruitersTab();
      case 'logs':
        return renderLogsTab();
      case 'alerts':
        return renderAlertsTab();
      default:
        return null;
    }
  };

  return (
    <div className="admin-view recruiter-assignment-view">
      {/* Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdAssignment className="title-icon" />
            Recruiter Assignment
          </h2>
        </div>
        <div className="header-filters-compact">
          {activeTab === 'recruiters' && (
            <>
              <div className="input search-compact">
                <span className="icon"><MdSearch /></span>
                <input
                  placeholder="Search recruiters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button className="btn-book-compact" onClick={loadRecruiters} title="Refresh">
                <MdRefresh />
              </button>
              <button className="btn-book-compact" onClick={openAddRecruiterModal}>
                <MdAdd />
                <span>Add Recruiter</span>
              </button>
            </>
          )}
          {activeTab === 'logs' && (
            <button className="btn-book-compact" onClick={loadLogs} title="Refresh">
              <MdRefresh />
            </button>
          )}
          {activeTab === 'alerts' && (
            <button className="btn-book-compact" onClick={loadAlerts} title="Refresh">
              <MdRefresh />
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
          >
            <tab.icon className="tab-icon" />
            <span>{tab.label}</span>
            {tab.id === 'alerts' && Array.isArray(alerts) && alerts.length > 0 && (
              <span className="tab-badge">{alerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Add/Edit Recruiter Modal */}
      {showRecruiterModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingRecruiter ? 'Edit Recruiter' : 'Add Recruiter'}</h3>
              <button className="modal-close" onClick={() => setShowRecruiterModal(false)}>×</button>
            </div>

            <form onSubmit={handleRecruiterSubmit} className="user-form">
              {!editingRecruiter && (
                <div className="form-group">
                  <label htmlFor="recruiterId">Select Recruiter *</label>
                  <select
                    id="recruiterId"
                    name="recruiterId"
                    value={recruiterForm.recruiterId || ''}
                    onChange={(e) => {
                      const selected = availableRecruiters.find(r => r.id === e.target.value);
                      setRecruiterForm(f => ({
                        ...f,
                        recruiterId: e.target.value,
                        recruiterName: selected ? `${selected.firstName} ${selected.secondName}` : '',
                        recruiterEmail: selected?.email || ''
                      }));
                    }}
                    required
                  >
                    <option value="">Select a recruiter</option>
                    {availableRecruiters.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.firstName} {r.secondName} ({r.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editingRecruiter && (
                <div className="form-group">
                  <label>Recruiter</label>
                  <div className="static-value">
                    <strong>{recruiterForm.recruiterName}</strong>
                    <span>{recruiterForm.recruiterEmail}</span>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="maxActiveJRs">Maximum Active JRs *</label>
                <input
                  type="number"
                  id="maxActiveJRs"
                  name="maxActiveJRs"
                  value={recruiterForm.maxActiveJRs || 15}
                  onChange={(e) => setRecruiterForm(f => ({ ...f, maxActiveJRs: parseInt(e.target.value) || 15 }))}
                  min="1"
                  max="100"
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={recruiterForm.participatesInAutoAssignment !== false}
                    onChange={(e) => setRecruiterForm(f => ({ ...f, participatesInAutoAssignment: e.target.checked }))}
                  />
                  <span>Participates in Auto-Assignment</span>
                </label>
              </div>

              {/* Vertical Mappings */}
              <div className="form-group">
                <label className="form-section-label">
                  <MdCategory style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Assigned Verticals
                </label>
                <p className="form-hint">Select verticals this recruiter will handle (for VERTICAL_BASED mode)</p>
                <div className="mapping-checkbox-grid">
                  {verticals.length === 0 ? (
                    <p className="no-data-hint">No verticals available. Add verticals in Master Data.</p>
                  ) : (
                    verticals.map(v => (
                      <label key={v.id} className="mapping-checkbox-label">
                        <input
                          type="checkbox"
                          checked={(recruiterForm.verticalIds || []).includes(v.id)}
                          onChange={() => toggleVerticalSelection(v.id)}
                        />
                        <span>{v.verticalName || v.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Project Mappings */}
              <div className="form-group">
                <label className="form-section-label">
                  <MdFolder style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Assigned Projects
                </label>
                <p className="form-hint">Select projects this recruiter will handle (overrides vertical mapping)</p>
                <div className="mapping-checkbox-grid">
                  {projects.length === 0 ? (
                    <p className="no-data-hint">No projects available. Add projects in Master Data.</p>
                  ) : (
                    projects.map(p => (
                      <label key={p.id} className="mapping-checkbox-label">
                        <input
                          type="checkbox"
                          checked={(recruiterForm.projectIds || []).includes(p.id)}
                          onChange={() => toggleProjectSelection(p.id)}
                        />
                        <span>{p.projectName || p.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-section-label">Notifications</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={recruiterForm.emailNotificationsEnabled !== false}
                      onChange={(e) => setRecruiterForm(f => ({ ...f, emailNotificationsEnabled: e.target.checked }))}
                    />
                    <span>Email Notifications</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={recruiterForm.inAppNotificationsEnabled !== false}
                      onChange={(e) => setRecruiterForm(f => ({ ...f, inAppNotificationsEnabled: e.target.checked }))}
                    />
                    <span>In-App Notifications</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowRecruiterModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingRecruiter ? 'Update' : 'Add Recruiter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Removal</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove <strong>{deleteConfirm.recruiterName}</strong> from auto-assignment?</p>
              <p style={{ color: '#6b7280', fontSize: '13px' }}>They will no longer receive automatically assigned JRs.</p>
            </div>
            <div className="form-actions">
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={handleDeleteRecruiter}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecruiterAssignmentConfig;
